import {
  IconFileBend,
  Inspector,
  GreyGrey70,
  useNotifications
} from '@sequencehq/core-components'
import { ServerAsset, useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { Attachments, showAttachmentInInspectorAtom } from 'Assets/Attachments'
import { useBillingScheduleAggregate } from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import { dequal } from 'dequal'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { ASSET_ACCEPTED_MIME_TYPES } from '@sequencehq/utils'
import { usePermissions } from 'lib/rbac'
import { withoutContractReferences } from 'modules/BillingSchedules/utils/withoutContractReferences'

const useScheduleAttachmentsInspector = (props: {
  billingScheduleId: string
}) => {
  const { displayNotification } = useNotifications()
  /**
   * We need a local representation of attachments, as the onChange
   * logic of the attachments inspector item will keep calling that
   * update if we don't keep this value up to date in an
   * optimistic fashion.
   */
  const [attachments, setAttachments] = useState<ServerAsset[]>([])

  /**
   * We grab the aggregate, alongside the basic schedule, because the aggregate
   * contains the resolved attachments too.
   */
  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId: props.billingScheduleId,
    select: data => {
      return {
        attachments: data.billingSchedule.attachmentAssets
      }
    }
  })

  /**
   * Keep the local state aligned with the service on a re-fetch, but retain
   * a local 'optimistic update' of the attachments.
   */
  useEffect(() => {
    if (billingScheduleAggregateQuery.data) {
      setAttachments(billingScheduleAggregateQuery.data.attachments)
    }
  }, [billingScheduleAggregateQuery.data])

  /**
   * Because we need to do a PUT (grumble grumble) we need the entire existing object,
   * The aggregate query parses dates for convenience, so rather than parse the dates
   * back or otherwise faff with the aggregate we just grab the basic object here too.
   * One day we'll hopefully have a PATCH!
   */
  const billingScheduleQuery = useQuery(
    dashboard20240730Client.getBillingSchedule,
    {
      id: props.billingScheduleId
    }
  )
  const scheduleMutator = useMutation(
    dashboard20240730Client.putBillingSchedule,
    {
      onError: () => {
        displayNotification('Could not save attachments', {
          type: 'error'
        })
        setAttachments(billingScheduleAggregateQuery.data?.attachments ?? [])
        scheduleMutator.reset()
      }
    }
  )

  const onAttachmentsChange = (newAttachments: ServerAsset[]) => {
    if (
      !billingScheduleQuery.data ||
      /**
       * If we've not yet updated our local state, don't do anything yet,
       * as we've new data inbound. This avoids a 'double call' of the put
       * in an error scenario.
       */
      !dequal(attachments, billingScheduleAggregateQuery.data?.attachments) ||
      scheduleMutator.isPending
    ) {
      return
    }

    if (
      dequal(
        newAttachments.map(a => a.id),
        attachments.map(a => a.id)
      )
    ) {
      return
    }

    setAttachments(newAttachments)

    const { customFields: _customFields, ...scheduleBody } =
      withoutContractReferences(billingScheduleQuery.data)
    scheduleMutator.mutate({
      id: props.billingScheduleId,
      body: {
        ...scheduleBody,
        attachmentAssetIds: newAttachments.map(attachment => attachment.id)
      }
    })
  }

  return {
    isLoading: billingScheduleAggregateQuery.isPending,
    attachments,
    onChange: onAttachmentsChange
  }
}

const InspectorItemContent = (props: { billingScheduleId: string }) => {
  const previewAttachmentFromAttachmentsSection = useAtomValue(
    showAttachmentInInspectorAtom
  )
  const { can } = usePermissions()
  const { attachments, onChange } = useScheduleAttachmentsInspector({
    billingScheduleId: props.billingScheduleId
  })

  const attachmentToView = useMemo(() => {
    if (!previewAttachmentFromAttachmentsSection) {
      return undefined
    }
    return attachments.find(
      attachment => attachment.id === previewAttachmentFromAttachmentsSection
    )
  }, [previewAttachmentFromAttachmentsSection, attachments])

  return (
    <Attachments
      disableModifyAttachments={
        can('org.billing:update')
          ? false
          : { reason: 'You do not have permission to update attachments' }
      }
      attachmentToPreview={attachmentToView ?? undefined}
      initialAttachments={attachments}
      onChange={onChange}
      acceptedMimeTypes={ASSET_ACCEPTED_MIME_TYPES.billingSchedules}
    />
  )
}

export const BillingScheduleAttachmentsInspectorItem = (props: {
  billingScheduleId: string
  id: string
}) => {
  return (
    <Inspector.IconItem
      id={props.id}
      data-testid="attachments.inspector.item"
      tooltip="Attachments"
      content={
        <InspectorItemContent billingScheduleId={props.billingScheduleId} />
      }
      icon={<IconFileBend color={GreyGrey70} />}
    />
  )
}
